import { FC, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ParentParams, Setting } from '../types/constant/PagesPropsType';
import discount from '../assets/images/cancel_discount.png';
import vip from '../assets/images/cancel_vip.png';
import cancelCushion from '../assets/scss/CancelCushion/CancelCushion.module.scss';
import { CancelSurveyModal } from '../components/CancelSurveyModal/CancelSurveyModal';
import { ChangeDeliveryDateModal } from '../components/ChangeDeliveryDateModal/ChangeDeliveryDateModal';
import { useCancelSurvey } from '../hooks/use-cancel-survey';
import { useSubscriptionDetail } from '../hooks/use-subscription-detail';
import { useUpdateNextDeliveryDate } from '../hooks/use-update-next-delivery-date';
import { useUpdateSubscriptionStatus } from '../hooks/use-update-subscription-status';
import { CancelSurveyModalViewType } from '../types/view/CancelSurveyModalViewType';
import { ChangeDeliveryDateModalViewType } from '../types/view/ChangeDeliveryDateModalViewType';
import { MessageModalViewType } from '../types/view/MessageModalViewType';
import { postMessageGtag } from '../utils/trackClick';
import { MessageModal } from '../components/MessageModal/MessageModal';
import { createChangeDeliveryDateModal } from '../utils/createChangeDeliveryDateModal';

export const CancellationCushion: FC<{
  setting: Setting;
  parentParams: ParentParams;
}> = ({ setting, parentParams }) => {
  // router hooks
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  // 画面内で呼び出されるAPI一覧
  const { subscription, setSubscription } = useSubscriptionDetail(setting, id);
  const { cancelSurvey } = useCancelSurvey(setting);
  console.log('cancelSurvey', cancelSurvey);
  const { updateDeliveryDays, validUpdateDeliveryDays } = useUpdateNextDeliveryDate();

  // 解約アンケートモーダルのデータ
  const [cancelSurveyModal, setCancelSurveyModal] = useState<CancelSurveyModalViewType | null>(
    null
  );
  // メッセージモーダルのデータ
  const [messageModal, setMessageModal] = useState<MessageModalViewType | null>(null);
  // お届け予定日変更モーダルのデータ
  const [changeDeliveryDateModal, setChangeDeliveryDateModal] =
    useState<ChangeDeliveryDateModalViewType | null>(null);
  // 定期登録済み次回お届け日
  const [registeredNextDeliveryDate, setRegisteredNextDeliveryDate] = useState<Date | null>(null);

  // リクエスト中に別のリクエストを飛ばさないためのフラグ
  const [loading, setLoading] = useState(false);

  const { cancelSubscription } = useUpdateSubscriptionStatus();
  // iframeの高さ調整
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setting.resize(root.current?.clientHeight ?? 0);
  }, [root.current?.clientHeight]);

  const handleOpenAlertModal = () => {
    if (!cancelSurvey) return;
    setCancelSurveyModal({
      leadText: `ご利用ありがとうございました。<br/ >解約理由について最も当てはまるものを1つ選択ください。`,
      hasExtraField: cancelSurvey.cancelSurveyExtraField,
      reasons: cancelSurvey.reasons.map((reason) => ({
        text: reason.text,
        selected: false,
      })),
    });
  };

  // お届け日時変更ボタンクリック時の挙動
  const handleCLickChangeDateBtn = () => {
    const deliveryDateData = createChangeDeliveryDateModal(subscription!);
    setChangeDeliveryDateModal(deliveryDateData);
    setRegisteredNextDeliveryDate(deliveryDateData.nextDeliveryDate);
  };

  useEffect(() => {
    if (!subscription) return;
    if (subscription.status === 'CANCELLED') {
      history.push('/');
      return;
    }
  }, [subscription]);

  useEffect(() => {
    window.parent.postMessage({ action: 'cancelCushion' }, '*');
  }, []);

  return (
    <>
      <div
        className={cancelCushion.cancelCushion}
        ref={root}
      >
        <section className={cancelCushion.section}>
          <h2 className={cancelCushion.heading}>解約手続きの前に</h2>
          <div>
            <p className={cancelCushion.description}>
              解約してしまうと、<span>定期オトク便の特典が利用できなくなります。</span>
            </p>
          </div>
        </section>
        <section className={cancelCushion.section}>
          <h3 className={cancelCushion.subHeading}>
            <span>オトクに他商品が買えなくなる</span>
          </h3>
          <div className={cancelCushion.imageWrapper}>
            <img
              src={discount}
              alt=""
            />
          </div>
          <p className={cancelCushion.caption}>
            10％OFFでお好きな商品を1個から追加できるサービスが使えなくなります。
            <br />
            さらにオトクになる定期オトク便の方限定のキャンペーンへも参加できなくなります。
          </p>
        </section>
        <section className={cancelCushion.section}>
          <h3 className={cancelCushion.subHeading}>
            <span>新商品の先行発売が利用できなくなる</span>
          </h3>
          <div className={cancelCushion.imageWrapper}>
            <img
              src={vip}
              alt=""
            />
          </div>
          <p className={cancelCushion.caption}>
            新商品を発売前にイチ早く試していただける先行販売を実施することがありますが、解約してしまうと案内が受け取れなくなってしまいます。
          </p>
        </section>
        <hr className={cancelCushion.divider} />
        <section className={cancelCushion.section}>
          <h2 className={cancelCushion.heading}>お届け日を見直す</h2>
          <p className={cancelCushion.description}>
            次回お届け6日前までは、お届け日の変更ができます。
            <br />
            60日後まで日付を変更できます。
          </p>
          <button
            className={cancelCushion.button__primary}
            onClick={() => {
              handleCLickChangeDateBtn();
              postMessageGtag('click_CancelPage_modal_open_date', 'preventCancel');
            }}
          >
            お届け日を変更する
          </button>
        </section>
        <hr className={cancelCushion.divider} />
        <section className={cancelCushion.section}>
          <dl className={cancelCushion.contact}>
            <dt className={cancelCushion.contact__label}>解約のご案内</dt>
            <dd className={cancelCushion.contact__info}>
              「解約を希望する」より解約の手続きをお願いします。
            </dd>
            <dd className={cancelCushion.contact__info}>
              初回限定の特別価格でお届けする為、ZENBの定期商品は、一世帯様一回限り（いずれか1セット）の制限を設けています。
            </dd>
            <dd className={cancelCushion.contact__info}>
              解約すると、残念ながらいずれの定期商品にも再度申し込むことができません。
            </dd>
            <dd className={cancelCushion.contact__info}>
              その為、定期のお届けを止める際に、解約以外にいつでも再開可能な「停止」の選択肢もご用意しました。
              <br />
              停止中はご請求も、商品のお届けも一切発生しません。
            </dd>
            <dd className={cancelCushion.contact__info}>
              再開する可能性がある場合は、いつでもマイアカウントで定期便を再開でき、再開後にお届け商品も自由に追加・変更※が可能な「停止」がおすすめです。
            </dd>
          </dl>
          <button
            className={cancelCushion.button__black}
            onClick={() => {
              handleOpenAlertModal();
              postMessageGtag('click_CancelPage_HopeCancel', 'preventCancel');
            }}
          >
            解約を希望する
          </button>
        </section>
      </div>
      {/* お届け日変更モーダル */}
      <ChangeDeliveryDateModal
        active={changeDeliveryDateModal !== null}
        closeFunc={() => {
          setChangeDeliveryDateModal(null);
          setRegisteredNextDeliveryDate(null);
        }}
        changeDeliveryDateModal={changeDeliveryDateModal}
        loading={loading}
        route={'CancellationCushion'}
        changeDeliveryDateFunc={(date) => {
          setChangeDeliveryDateModal({
            ...changeDeliveryDateModal!,
            nextDeliveryDate: date,
          });
        }}
        changeDeliveryTimeFunc={(deliveryTime) => {
          setChangeDeliveryDateModal({
            ...changeDeliveryDateModal!,
            deliveryTime,
          });
        }}
        clickUpdateBtnFunc={() => {
          const errors = validUpdateDeliveryDays(subscription!);
          if (errors.length > 0) {
            setMessageModal({
              message: errors[0],
            });

            return;
          }

          setLoading(true);
          // お届け日のみ変更
          updateDeliveryDays(
            setting,
            subscription!,
            changeDeliveryDateModal!,
            (nextBillingDate, nextDeliveryDate, deliveryDays, deliveryTime, deliveryTimeText) => {
              setSubscription({
                ...subscription!,
                nextBillingDate,
                nextDeliveryDate,
                deliveryDays,
                deliveryTime,
                deliveryTimeText,
              });
              setMessageModal({
                message: '次回お届け予定日を更新しました',
              });
            },
            () =>
              setMessageModal({
                message: '次回お届け予定日の更新に失敗しました',
              }),
            () => {
              setLoading(false);
              setChangeDeliveryDateModal(null);
              setRegisteredNextDeliveryDate(null);
            }
          );
        }}
      />
      {/* 解約モーダル */}
      <CancelSurveyModal
        active={cancelSurveyModal !== null}
        closeFunc={() => setCancelSurveyModal(null)}
        cancelSurveyModal={cancelSurveyModal}
        clickSelectFunc={(num) => {
          setCancelSurveyModal({
            ...cancelSurveyModal!,
            reasons: cancelSurveyModal!.reasons.map((reason, idx) => {
              return {
                ...reason,
                selected: idx === num,
              };
            }),
          });
        }}
        changeExtraFiledFunc={(text) =>
          setCancelSurveyModal({
            ...cancelSurveyModal!,
            extraField: text,
          })
        }
        clickSubmitFunc={() => {
          setLoading(true);
          cancelSubscription(
            setting,
            subscription!,
            cancelSurveyModal!,
            () => {
              setCancelSurveyModal(null);
              history.push('/');
            },
            () => {
              setMessageModal({
                message: '解約に失敗しました',
              });
            },
            () => {
              setLoading(false);
            }
          );
        }}
        loading={loading}
      />
      {/* メッセージのみ表示するモーダル */}
      <MessageModal
        active={messageModal !== null}
        closeFunc={() => setMessageModal(null)}
        messageModal={messageModal}
      />
    </>
  );
};
